/**
 * 인증 클라이언트 상태 스토어.
 *
 * 서버 상태(`/auth/me` 응답)는 React Query 가 소스-오브-트루스이다.
 * 이 스토어는 **파생된 UI 상태** 만 보관한다:
 *   - `user`   : 최근 me 쿼리 결과 (또는 로그인 뮤테이션 결과)
 *   - `status` : 3-상태 머신 (`idle` / `authenticated` / `unauthenticated`)
 *
 * 리듀서는 없다 — 호출측(`useAuth`, 뮤테이션 onSuccess) 에서 `setUser(null|user)`
 * 로만 업데이트. `setUser(null)` 은 `unauthenticated`, `setUser(user)` 는
 * `authenticated` 로 전이한다. 초기 렌더 시점에는 `idle` 로 남겨두어야
 * 리다이렉트 가드가 "아직 모름" 을 구분할 수 있다.
 */

import { create } from 'zustand';

export interface AuthImpersonationActor {
  id: number;
  email: string;
  name: string | null;
}

export interface AuthImpersonation {
  active: true;
  actor: AuthImpersonationActor;
  expires_at: number;
  can_return_to_admin: boolean;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  credits: number;
  impersonation?: AuthImpersonation | null;
  ui_theme?: string | null;
}

export type AuthStatus = 'idle' | 'authenticated' | 'unauthenticated';

export interface AuthStore {
  user: AuthUser | null;
  status: AuthStatus;
  pendingCreditReservationIds: string[];
  localHeldSeparationIds: number[];
  setUser: (user: AuthUser | null) => void;
  beginPendingCreditReservation: (reservationId: string) => void;
  resolvePendingCreditReservation: (reservationId: string, separationId: number) => void;
  rejectPendingCreditReservation: (reservationId: string) => void;
  settleLocalCreditHold: (
    separationId: number,
    outcome: 'completed' | 'failed',
  ) => void;
  /** 명시적으로 "아직 모름" 으로 되돌리고 싶을 때 (테스트/로그아웃 중 일시상태). */
  reset: () => void;
}

function dedupeReservationIds(ids: string[], nextId: string): string[] {
  return ids.includes(nextId) ? ids : [...ids, nextId];
}

function dedupeSeparationIds(ids: number[], nextId: number): number[] {
  return ids.includes(nextId) ? ids : [...ids, nextId];
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  status: 'idle',
  pendingCreditReservationIds: [],
  localHeldSeparationIds: [],
  setUser: (user) =>
    set((state) => ({
      user,
      status: user === null ? 'unauthenticated' : 'authenticated',
      pendingCreditReservationIds:
        user === null ? [] : state.pendingCreditReservationIds,
      localHeldSeparationIds:
        user === null ? [] : state.localHeldSeparationIds,
    })),
  beginPendingCreditReservation: (reservationId) =>
    set((state) => ({
      pendingCreditReservationIds: dedupeReservationIds(
        state.pendingCreditReservationIds,
        reservationId,
      ),
    })),
  resolvePendingCreditReservation: (reservationId, separationId) =>
    set((state) => {
      const pendingCreditReservationIds = state.pendingCreditReservationIds.filter(
        (id) => id !== reservationId,
      );
      if (!state.user) {
        return {
          pendingCreditReservationIds,
          localHeldSeparationIds: dedupeSeparationIds(
            state.localHeldSeparationIds,
            separationId,
          ),
        };
      }
      return {
        user: {
          ...state.user,
          credits: Math.max(state.user.credits - 1, 0),
        },
        pendingCreditReservationIds,
        localHeldSeparationIds: dedupeSeparationIds(
          state.localHeldSeparationIds,
          separationId,
        ),
      };
    }),
  rejectPendingCreditReservation: (reservationId) =>
    set((state) => ({
      pendingCreditReservationIds: state.pendingCreditReservationIds.filter(
        (id) => id !== reservationId,
      ),
    })),
  settleLocalCreditHold: (separationId, outcome) =>
    set((state) => {
      const localHeldSeparationIds = state.localHeldSeparationIds.filter(
        (id) => id !== separationId,
      );
      if (!state.user || outcome !== 'failed') {
        return { localHeldSeparationIds };
      }
      return {
        user: {
          ...state.user,
          credits: state.user.credits + 1,
        },
        localHeldSeparationIds,
      };
    }),
  reset: () =>
    set({
      user: null,
      status: 'idle',
      pendingCreditReservationIds: [],
      localHeldSeparationIds: [],
    }),
}));

export function selectPendingCreditReservationCount(state: AuthStore): number {
  return state.pendingCreditReservationIds.length;
}

export function selectDisplayedCredits(state: AuthStore): number | null {
  if (!state.user) return null;
  return Math.max(
    state.user.credits - state.pendingCreditReservationIds.length,
    0,
  );
}
